import { useDataSource } from './use-data-source';
import { buildAst } from '@/utils/ast';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Shape, ShapeType } from '@/services/shape';
import { WatchfaceService } from '@/services/watchface';
import { xmlToAst } from '@/utils/xmlTool';
import { useLayerStore } from '@/store/use-layer-store';
import { useImageDoms } from './use-image-doms';
import { useImagesStore } from '@/store/use-images-store';
import { WatchfaceRenderer } from '@/watch-renderer';
import { useShapeVideo } from './use-shape-video';
import { mainRenderer } from './use-canvas-render';
import { getImageSrcs } from '@/utils/ast/utils/resource-image.utils';
import { useDataSourceStore } from '@/store/use-data-source-store';
import { canUse } from '@/utils/can-use.utils';
import { LayoutItemMap } from '@/watch-renderer/layout';
import generateID from '@/utils/generate-id';
import { LayoutBase } from '@/watch-renderer/layout/LayoutBase';

export interface LayoutRenderAttr {
  id: string;
  size: { width: number; height: number };
  posi: LayoutBase['posi'];
}

const _existsPointer = (theme?: StoreThemeItem) => {
  const stack = [...(theme?.children ?? [])];
  let hour = false;
  let minute = false;
  let second = false;
  while (stack.length) {
    const layer = stack.pop();
    if (layer?.children) stack.unshift(...layer.children);
    const source = layer?.attrs?.source;
    if (layer?.type == ShapeType.Image) {
      if (source == 'timeHour' || source == 'time') hour = true;
      if (source == 'timeMinute') minute = true;
      if (source == 'timeSecond') second = true;
    }
  }

  return [hour, minute, second].filter((item) => !!item).length > 1;
};

export const useWatchfaceRenderAttr = () => {
  let canvasEl: HTMLCanvasElement;
  let renderer: WatchfaceRenderer;

  const watchfaceStore = useWatchfaceStore();
  const layerStore = useLayerStore();
  const imageStore = useImagesStore();
  const dataSourceStore = useDataSourceStore();
  const { configData } = useDataSource();
  const { imgsToDoms } = useImageDoms();
  const { toVideoDoms, getVideoSrcsFromAst } = useShapeVideo();

  /**
   * 获取表盘所有shape的渲染尺寸（无rive）
   * @param watchfacePath [可选] 需要渲染的表盘目录，默认为空，即用当前打开的表盘数据
   * @param shapeIds 需要渲染的图层id,默认为空，渲染全部数据
   * @returns
   */
  const getWatchfaceRenderAttr = async (
    watchfacePath?: string,
    options?: {
      themeName?: string;
      themeType?: 'normal' | 'AOD';
      /**
       * 配色：默认使用第一个配色
       */
      themeColor?: string;
      shapeIds?: string[];
      slotDisplayAll?: WatchRendererOptions['renderTheme']['slotDisplayAll'];
      ignoreBg?: boolean;
      usingCurConfigData?: boolean;
      clipRound?: number;
      usePreviewPointerData?: boolean;
      forceRenderWhenHidden?: boolean;
      returnType?: 'base64' | 'canvas';
      // 忽略所有的旋转
      ignoreRotation?: boolean;
      useMaxValue?: boolean;
    }
  ) => {
    canvasEl = canvasEl ?? document.createElement('canvas');
    if (!canvasEl) return;
    const {
      themeName,
      themeColor,
      themeType = 'normal',
      shapeIds = undefined,
      slotDisplayAll,
      usingCurConfigData = false,
      usePreviewPointerData = false,
      forceRenderWhenHidden = false,
      ignoreRotation = false,
      useMaxValue = false
    } = options ?? {};

    const usingCurentWatchface = watchfacePath ? false : true;

    const service = new WatchfaceService(
      watchfacePath ? watchfacePath : watchfaceStore.path
    );
    const _getAst = async () => {
      if (usingCurentWatchface) {
        const ast = mainRenderer?.options.ast;
        const themes = ast?.children.filter((item) => item.tag == 'Theme');
        // 判断是否可使用旧ast
        const existsTheme =
          !mainRenderer?.runtime.optionsDirty &&
          themes?.some(
            (item) =>
              item.attrsMap.type == themeType && item.attrsMap.name == themeName
          );
        return existsTheme
          ? ast
          : buildAst({
              buildFor: 'editor',
              themeName,
              themeType
            });
      } else {
        const manifestXml = await service.manifest.readManifestXML();
        return xmlToAst(manifestXml ?? '');
      }
    };

    const _getImageDoms = async (ast: Ast) => {
      const _images = usingCurentWatchface
        ? imageStore.imgs
        : (await service.resource.getImagesData()) ?? [];

      const basenames = getImageSrcs(ast);

      const images: StoreResourceItem[] = [];
      _images.forEach((item) => {
        // return str.includes(item.basename);
        if (basenames.has(item.basename)) {
          images.push(item);
        }
      });

      const imgs = await imgsToDoms({
        watchfaceAst: ast,
        imgs: images,
        cached: mainRenderer?.runtime.images
      });

      return imgs;
    };

    const ast = (await _getAst()) ?? {
      tag: 'Watchface',
      attrsMap: {},
      children: []
    };

    const imgs = await _getImageDoms(ast);

    // 获取 vidoe doms数据
    const videoSrcs = getVideoSrcsFromAst(ast, watchfacePath);
    const videos = await toVideoDoms(videoSrcs, {
      autoLoadData: true
    });

    const desc = usingCurentWatchface
      ? watchfaceStore.description
      : await service.description.getDescriptionData();

    // 查找样式
    const theme = ast.children.find(
      (item) =>
        item.tag == 'Theme' &&
        item.attrsMap.type == themeType &&
        (themeName ? item.attrsMap.name == themeName : true)
    ) ?? {
      tag: 'Theme',
      attrsMap: {},
      children: []
    };

    // 主题配色
    const isColorTableMode = (desc?.watchfaceType ?? 'normal') == 'normal';
    const colorTable = isColorTableMode
      ? theme.attrsMap['colorGroupTable']
      : theme.attrsMap['recolorTable'];
    const colorGroupTable = colorTable?.split(',') ?? [];
    const colorIndex = colorGroupTable.findIndex((item) => item == themeColor);

    const renderTheme: WatchRendererOptions['renderTheme'] = {
      name: theme.attrsMap.name,
      type: theme.attrsMap.type as 'normal',
      colorMode: isColorTableMode ? 'colorGroup' : 'recolor',
      // 规则：当是colorGroup时，如果有多个配色，则需要添加配色值
      // 如果是recolor，当配色值为第一个配色时，无需设置配色值
      themeColor: isColorTableMode
        ? colorGroupTable.length > 1
          ? themeColor ?? colorGroupTable[0]
          : undefined
        : colorIndex > 0
        ? themeColor
        : undefined,
      slotDisplayAll,
      ignoreRotation
    };

    if (shapeIds && usingCurentWatchface) {
      const renderLayoutIds: string[] = [];

      // 将所有父级元素加上
      const shapeIds1 = [...shapeIds];
      while (shapeIds1.length) {
        const shapeId = shapeIds1.shift() ?? '';
        const shape = Shape.getShape(shapeId);
        if (!shape) continue;
        renderLayoutIds.push(shape.id);
        if (shape?.parent) shapeIds1.push(shape?.parent.id);
      }

      // 将所有子元素加上
      const shapeIds2 = [...shapeIds];
      while (shapeIds2.length) {
        const shapeId = shapeIds2.shift() ?? '';
        const shape = Shape.getShape(shapeId);
        if (!shape) continue;
        renderLayoutIds.push(shape.id);
        if (shape?.children?.length)
          shapeIds2.push(...shape.children.map((item) => item.id));
      }

      renderTheme.renderLayoutIds = [...new Set(renderLayoutIds)];
    }

    const _getDataSource = async () => {
      if (usingCurentWatchface) {
        return dataSourceStore.wholeValues(undefined);
      } else {
        const defaultValues = await service.editorConfig
          .getEditorData()
          .then((data) => data?.dataSource ?? {});

        return dataSourceStore.wholeValues(defaultValues);
      }
    };

    const defaultConfigData = await _getDataSource();

    const _getDataMaxValue = () => {
      if (!shapeIds?.length) return;
      const shape = Shape.getShape(shapeIds[0]);
      if (!shape) return;
      shape.soueceKeys.forEach((key, index) => {
        const maxValue = shape.sourceItems[index]?.maxValue;
        if (maxValue) defaultConfigData[key] = maxValue;
      });
    };

    if (useMaxValue) _getDataMaxValue();

    // 处理预览图中数据源
    if (usePreviewPointerData) {
      const __existsPointer = _existsPointer(
        layerStore.themes.find(
          (item) =>
            item.name == renderTheme.name && item.type == renderTheme.type
        )
      );
      // 如果存在指针需要使用10:09:24数据源，其他情况用09:28:00
      if (
        __existsPointer ||
        canUse('default-time-change', watchfaceStore.deviceType).valid
      ) {
        defaultConfigData['timeHour'] = 10;
        defaultConfigData['timeMinute'] = 9;
        defaultConfigData['timeSecond'] = 24;
      }
      if (canUse('default-time-change', watchfaceStore.deviceType).valid) {
        defaultConfigData['dualtimeHour'] = 10;
        defaultConfigData['dualtimeMinute'] = 9;
        defaultConfigData['dualtimeSecond'] = 24;
      }
    }

    const config: WatchRendererOptions = {
      ast: ast,
      images: imgs,
      videos: videos,
      configData: configData.value, // usingCurConfigData ? configData.value : defaultConfigData,
      renderTheme,
      scale: 1,
      ignoreBg: options?.ignoreBg,
      clipRound: options?.clipRound,
      forceShowHidden: !!forceRenderWhenHidden
    };

    // 获取宽度和高度
    const [width, height] = desc?.size?.split('x') ?? [];
    canvasEl.width = Number(width);
    canvasEl.height = Number(height);

    renderer = renderer ?? new WatchfaceRenderer(canvasEl, config);
    if (!renderer) return;
    renderer.reset(config);

    const res: LayoutRenderAttr[] = [];

    const getRenderSize = (
      layouts: ILayout[],
      parentPosi?: LayoutBase['posi']
    ) => {
      layouts
        ?.filter((item) => ['Layout', 'Item'].includes(item.tagType))
        .filter((item) => {
          // 过滤需要渲染的shape id
          const { _id } = item;
          const { renderLayoutIds } = renderer.options.renderTheme;
          return renderLayoutIds
            ? renderLayoutIds.some((item) => !!_id && _id.includes(item))
            : true;
        })
        .forEach((layout: ILayout) => {
          // 判断是否需要绘制

          // 子元素的位置为相对位置，在绘制时需要处理为绝对位置
          const { x: x0 = 0, y: y0 = 0 } = parentPosi || {};
          const {
            x: x1 = 0,
            y: y1 = 0,
            ref = '',
            _id = generateID(),
            _scale = 1
          } = layout;
          // 元素位置
          const posi: LayoutBase['posi'] = {
            x: Math.floor(x0 + x1),
            y: Math.floor(y0 + y1),
            offsetX: Math.floor(x1),
            offsetY: Math.floor(y1)
          };

          // 元素数据
          const dataItem = renderer.runtime.dataItems[ref];
          const type = dataItem?.tagType;
          const _Layout = LayoutItemMap[type as 'Image'];
          if (!type || !_Layout) {
            return;
          }

          const _layout = new _Layout(renderer, dataItem, posi, _id, _scale);
          _layout.draw();

          if (type === 'Widget' || type === 'Slot') {
            const node = _layout.dataItem as IWidget;
            const childern = node.children;
            getRenderSize(childern, _layout.getLayoutPosi());
          }

          // 成指针后runtimeState可能为空
          res.push({
            id: _layout.getLayoutId(),
            size: _layout.getRuntimeState() || _layout.getSize(),
            posi: _layout.getLayoutPosi()
          });
        });
    };
    getRenderSize(renderer.runtime.layouts);
    return res;
  };

  return { getWatchfaceRenderAttr };
};
